/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Button from '@mui/material/Button';

import { RootState } from '../../app/store';
import pearlApi, { CustomAxiosRequestConfig } from '../sharedcomponents/api/pearlApi';
import { snackNotifications } from '../sharedcomponents/notification/SnackBarUtils';
import {
  credentialResultType,
  EnrollmentStateType,
  EnrollmentUserType,
  ExcelRowType,
  RowType,
} from './AffiliateAdminTypes';
import { downloadSampleTemplate } from './BulkUploadReportDownload';

const config: CustomAxiosRequestConfig = {
  skipInterceptor: true, // Skip the interceptor for this request
};

const withTargetedOrg = (url: string, targetedOrgId?: string) => {
  if (!targetedOrgId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}targeted_org_id=${encodeURIComponent(targetedOrgId)}`;
};

export const initialState: EnrollmentStateType = {
  sendingCredentials: false,
  updatingUserState: false,
  isBulkEditing: false,
  bulkEditFileName: '',
  bulkEditReport: [],
  bulkUploadCount: 0,
};

export const fetchUsersForEnrollment = createAsyncThunk<
  { count: number; results: EnrollmentUserType[] },
  {
    pn: number;
    ps: number;
    sortParams?: {
      oc: string;
      dr: string;
    };
    filterParams?: Record<string, any>;
  },
  { state: { userEnrollment: EnrollmentStateType } }
>('userEnrollment/fetchUsersForEnrollment', async ({ pn, ps, sortParams, filterParams }, thunkAPI) => {
  const targetedOrgId = thunkAPI.getState().userEnrollment.targetedOrgId;
  const searchParams = new URLSearchParams(
    Object.fromEntries(
      Object.entries({
        pn: String(pn + 1),
        ps: String(ps),
        ...sortParams,
        ...filterParams,
        targeted_org_id: targetedOrgId,
      }).filter(([, v]) => v !== undefined)
    ) as Record<string, string>
  );
  const {
    data: { data },
  } = await pearlApi.get(`/enrollment_requests?${searchParams.toString()}`);
  return data;
});

export const sendCredential = createAsyncThunk<
  {
    success: credentialResultType;
    fail: credentialResultType;
  },
  void,
  { state: { userEnrollment: EnrollmentStateType } }
>('userEnrollment/sendCredential', async (_, thunkAPI) => {
  const enrollmentRequestId = thunkAPI.getState().userEnrollment.selectedUser?.id;
  const {
    data: { data },
  }: {
    data: {
      data: credentialResultType;
    };
  } = await pearlApi.post(`/enrollment_credentials?enrollment_request_ids=${enrollmentRequestId}`);

  const result: {
    success: credentialResultType;
    fail: credentialResultType;
  } = {
    success: {},
    fail: {},
  };

  for (const [key, value] of Object.entries(data)) {
    if (value) {
      result.success[key] = value;
    } else {
      result.fail[key] = value;
    }
  }
  return result;
});

export const updateBulkUsersForEnrollment = createAsyncThunk<
  number,
  Partial<RowType>[],
  { state: { userEnrollment: EnrollmentStateType } }
>('userEnrollment/updateBulkUsersForEnrollment', async (users: Partial<RowType>[], { dispatch, getState }) => {
  dispatch(setBulkUploadCount(users.length));
  const targetedOrgId = getState().userEnrollment.targetedOrgId;

  const CONCURRENT_REQUEST_LIMIT = 4;
  let failCount = 0;

  const queue = new Set<Promise<void>>();

  const updateUser = async (user: Partial<RowType>) => {
    const excelResult: Partial<ExcelRowType> = {
      'First Name': user.given_name,
      'Last Name': user.family_name,
      Email: user.email,
      NPI: user.npi,
      Specialty: user.specialty,
      Network: user.network,
      'Profession (Degree)': user.degree,
      'Medical Group': user.medical_group,
      'PCP (Yes/No)': (() => {
        if (user.pcp?.toLowerCase() === 'true') return 'Yes';
        if (user.pcp?.toLowerCase() === 'false') return 'No';
      })(),
    };
    try {
      const { data } = await pearlApi.put(
        withTargetedOrg(`/user_profiles/by_email`, targetedOrgId),
        { data: user },
        config
      );

      if (data.errors) {
        failCount++;
        dispatch(
          addToBulkEditReport({
            ...excelResult,
            Errors: data.errors[0]?.title || 'Unknown error',
            Status: 'Fail',
          })
        );
      } else {
        dispatch(addToBulkEditReport({ ...excelResult, Status: 'Success' }));
      }
    } catch (error: any) {
      failCount++;
      dispatch(
        addToBulkEditReport({
          ...excelResult,
          Errors: error?.message || 'Unknown error',
          Status: 'Fail',
        })
      );
    }
  };

  for (const user of users) {
    const task = updateUser(user).finally(() => queue.delete(task));
    queue.add(task);

    if (queue.size >= CONCURRENT_REQUEST_LIMIT) {
      await Promise.race(queue);
    }
  }

  await Promise.all(queue);

  return failCount;
});

export const addUser = createAsyncThunk<
  EnrollmentUserType,
  Partial<EnrollmentUserType>,
  { state: { userEnrollment: EnrollmentStateType } }
>('userEnrollment/addUser', async (user, thunkAPI) => {
  const targetedOrgId = thunkAPI.getState().userEnrollment.targetedOrgId;
  const trimUserValues = Object.fromEntries(
    Object.entries(user).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
  );

  const { data } = await pearlApi.post(
    withTargetedOrg(`/enrollment_requests`, targetedOrgId),
    { data: trimUserValues },
    config
  );
  if (data.errors) {
    const errorMessage = data.errors[0].title?.toLowerCase().includes('email')
      ? 'Email is already associated with a user account.'
      : `Failed to add ${user.given_name}(${user.email}).`;
    throw new Error(errorMessage);
  }
  return data.data;
});

export const updateUserData = createAsyncThunk<
  any,
  EnrollmentUserType,
  { state: { userEnrollment: EnrollmentStateType } }
>('enrollment/update', async (user: EnrollmentUserType, thunkAPI) => {
  try {
    const targetedOrgId = thunkAPI.getState().userEnrollment.targetedOrgId;
    const data: Partial<EnrollmentUserType> = {
      email: user.email,
      npi: user.npi,
      pcp: user.pcp,
      specialty: user.specialty,
      medical_group: user.medical_group,
      network: user.network,
      degree: user.degree,
    };
    const res = await pearlApi.put(withTargetedOrg(`/user_profiles/by_email`, targetedOrgId), { data });
    return res;
  } catch (err: any) {
    console.error(err);
  }
});

export const userEnrollmentSlice = createSlice({
  name: 'userEnrollment',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<EnrollmentUserType | undefined>) => {
      state.selectedUser = action.payload;
    },
    setBulkEditFileName: (state, action) => {
      state.bulkEditFileName = action.payload;
    },
    addToBulkEditReport: (state, action) => {
      state.bulkEditReport.push(action.payload);
    },
    setBulkUploadCount: (state, action: PayloadAction<number>) => {
      state.bulkUploadCount = action.payload;
    },
    setTargetedOrgId: (state, action: PayloadAction<string | undefined>) => {
      state.targetedOrgId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUser.fulfilled, (state, action: PayloadAction<EnrollmentUserType>) => {
        const user = action.payload;
        snackNotifications.success(`User ${user.given_name} (${user.email}) added successfully!`);
      })
      .addCase(addUser.rejected, (_, action) => {
        const errorMsg = action.error.message;
        snackNotifications.error(errorMsg);
      })
      .addCase(sendCredential.pending, (state) => {
        state.sendingCredentials = true;
      })
      .addCase(sendCredential.rejected, (state) => {
        state.sendingCredentials = false;
        snackNotifications.error('Failed to deliver login credentials to the selected user.');
      })
      .addCase(sendCredential.fulfilled, (state, data) => {
        state.sendingCredentials = false;
        if (data.payload.fail[state.selectedUser?.id || '']) {
          snackNotifications.error('Failed to deliver login credentials to the selected user.');
        } else {
          state.selectedUser = undefined;
          snackNotifications.success('Login credentials have been successfully sent to the selected user!');
        }
      })
      .addCase(updateBulkUsersForEnrollment.pending, (state) => {
        state.isBulkEditing = true;
        state.bulkEditReport = [];
      })
      .addCase(updateBulkUsersForEnrollment.fulfilled, (state, data) => {
        state.isBulkEditing = false;
        const { bulkEditFileName, bulkEditReport } = state;
        const totalRecords = bulkEditReport.length || 0;
        const failedRecords = data.payload || 0;
        const successfulRecords = totalRecords - failedRecords;
        const safeData = JSON.parse(JSON.stringify(bulkEditReport));

        // Show success message
        if (successfulRecords > 0) {
          snackNotifications.success(`${successfulRecords} record(s) updated successfully!`);
        }

        // Show error message only if there are failed updates
        if (failedRecords > 0) {
          snackNotifications.error(`Oops! ${failedRecords} record(s) failed to update.`, {
            autoHideDuration: 12000,
            action: (
              <Button
                variant="link"
                size="small"
                onClick={() => downloadSampleTemplate(safeData, 'REPORT_' + bulkEditFileName)}
              >
                Download report
              </Button>
            ),
          });
        }
      })
      .addCase(updateBulkUsersForEnrollment.rejected, (state) => {
        state.isBulkEditing = false;
        snackNotifications.error(`Oops! Record(s) failed to update.`);
      })
      .addCase(updateUserData.pending, (state) => {
        state.updatingUserState = true;
      })
      .addCase(updateUserData.rejected, (state) => {
        state.updatingUserState = false;
        snackNotifications.error('Failed to update user details.');
      })
      .addCase(updateUserData.fulfilled, (state) => {
        state.updatingUserState = false;
      });
  },
});

export const { setSelectedUser, setBulkEditFileName, addToBulkEditReport, setBulkUploadCount, setTargetedOrgId } =
  userEnrollmentSlice.actions;

export const selectUserEnrollmentSendingCredentials = (state: RootState) => state.userEnrollment.sendingCredentials;
export const selectUpdatingUserData = (state: RootState) => state.userEnrollment.updatingUserState;
export const selectSelectedUser = (state: RootState) => state.userEnrollment.selectedUser;

export const selectIsBulkEditing = (state: RootState) => state.userEnrollment.isBulkEditing;
export const selectBulkEditFileName = (state: RootState) => state.userEnrollment.bulkEditFileName;
export const selectBulkUploadCount = (state: RootState) => state.userEnrollment.bulkUploadCount;
export const selectBulkEditReport = (state: RootState) => state.userEnrollment.bulkEditReport;
export const selectTargetedOrgId = (state: RootState) => state.userEnrollment.targetedOrgId;

export default userEnrollmentSlice.reducer;
